/**
 * DevExtreme (esm/__internal/grids/grid_core/filter/m_filter_row.js)
 * Version: 23.2.10
 * Build date: Mon Oct 28 2024
 *
 * Copyright (c) 2012 - 2024 Developer Express Inc. ALL RIGHTS RESERVED
 * Read about DevExtreme licensing here: https://js.devexpress.com/Licensing/
 */
import _extends from "@babel/runtime/helpers/esm/extends";
import $ from "../../../../core/renderer";
import {
    equalByValue
} from "../../../../core/utils/common";
import {
    extend
} from "../../../../core/utils/extend";
import {
    each,
    map
} from "../../../../core/utils/iterator";
import {
    getOuterWidth
} from "../../../../core/utils/size";
import {
    isDefined
} from "../../../../core/utils/type";
import eventsEngine from "../../../../events/core/events_engine";
import {
    normalizeKeyName
} from "../../../../events/utils/index";
import messageLocalization from "../../../../localization/message";
import Editor from "../../../../ui/editor/editor";
import Menu from "../../../../ui/menu";
import Overlay from "../../../../ui/overlay/ui.overlay";
import {
    selectView
} from "../../../../ui/shared/accessibility";
import modules from "../m_modules";
import gridCoreUtils from "../m_utils";
var OPERATION_ICONS = {
    "=": "filter-operation-equals",
    "<>": "filter-operation-not-equals",
    "<": "filter-operation-less",
    "<=": "filter-operation-less-equal",
    ">": "filter-operation-greater",
    ">=": "filter-operation-greater-equal",
    default: "filter-operation-default",
    notcontains: "filter-operation-not-contains",
    contains: "filter-operation-contains",
    startswith: "filter-operation-starts-with",
    endswith: "filter-operation-ends-with",
    between: "filter-operation-between"
};
var OPERATION_DESCRIPTORS = {
    "=": "equal",
    "<>": "notEqual",
    "<": "lessThan",
    "<=": "lessThanOrEqual",
    ">": "greaterThan",
    ">=": "greaterThanOrEqual",
    startswith: "startsWith",
    contains: "contains",
    notcontains: "notContains",
    endswith: "endsWith",
    between: "between"
};
var FILTERING_TIMEOUT = 700;
var CORRECT_FILTER_RANGE_OVERLAY_WIDTH = 1;
var FILTER_ROW_CLASS = "filter-row";
var FILTER_RANGE_OVERLAY_CLASS = "filter-range-overlay";
var FILTER_RANGE_START_CLASS = "filter-range-start";
var FILTER_RANGE_END_CLASS = "filter-range-end";
var MENU_CLASS = "dx-menu";
var EDITOR_WITH_MENU_CLASS = "dx-editor-with-menu";
var EDITOR_CONTAINER_CLASS = "dx-editor-container";
var EDITOR_CELL_CLASS = "dx-editor-cell";
var FILTER_MENU = "dx-filter-menu";
var APPLY_BUTTON_CLASS = "dx-apply-button";
var HIGHLIGHT_OUTLINE_CLASS = "dx-highlight-outline";
var FOCUSED_CLASS = "dx-focused";
var CELL_FOCUS_DISABLED_CLASS = "dx-cell-focus-disabled";
var FILTER_RANGE_CONTENT_CLASS = "dx-filter-range-content";
var FILTER_MODIFIED_CLASS = "dx-filter-modified";
var EDITORS_INPUT_SELECTOR = "input:not([type='hidden'])";
var BETWEEN_OPERATION_DATA_TYPES = ["date", "datetime", "number"];
var ARIA_SEARCH_BOX = messageLocalization.format("dxDataGrid-ariaSearchBox");

function isOnClickApplyFilterMode(that) {
    return "onClick" === that.option("filterRow.applyFilter")
}
var getEditorInstance = function($editorContainer) {
    var $editor = $editorContainer && $editorContainer.children();
    var componentNames = $editor && $editor.data("dxComponents");
    var editor = componentNames && componentNames.length && $editor.data(componentNames[0]);
    if (editor instanceof Editor) {
        return editor
    }
    return null
};
var getRangeTextByFilterValue = function(that, column) {
    var result = "";
    var rangeEnd = "";
    var filterValue = getColumnFilterValue(that, column);
    var formatOptions = gridCoreUtils.getFormatOptionsByColumn(column, "filterRow");
    if (Array.isArray(filterValue)) {
        result = gridCoreUtils.formatValue(filterValue[0], formatOptions);
        rangeEnd = gridCoreUtils.formatValue(filterValue[1], formatOptions);
        if ("" !== rangeEnd) {
            result += " - ".concat(rangeEnd)
        }
    } else if (isDefined(filterValue)) {
        result = gridCoreUtils.formatValue(filterValue, formatOptions)
    }
    return result
};

function getColumnFilterValue(that, column) {
    if (column) {
        return isOnClickApplyFilterMode(that) && void 0 !== column.bufferedFilterValue ? column.bufferedFilterValue : column.filterValue
    }
}
var getColumnSelectedFilterOperation = function(that, column) {
    if (column) {
        return isOnClickApplyFilterMode(that) && void 0 !== column.bufferedSelectedFilterOperation ? column.bufferedSelectedFilterOperation : column.selectedFilterOperation
    }
};
var isValidFilterValue = function(filterValue, column) {
    if (column && BETWEEN_OPERATION_DATA_TYPES.includes(column.dataType) && Array.isArray(filterValue)) {
        return false
    }
    return void 0 !== filterValue
};
var getFilterValue = function(that, columnIndex, $editorContainer) {
    var column = that._columnsController.columnOption(columnIndex);
    var filterValue = getColumnFilterValue(that, column);
    var isFilterRange = $editorContainer.closest(".".concat(that.addWidgetPrefix(FILTER_RANGE_OVERLAY_CLASS))).length;
    var isRangeStart = $editorContainer.hasClass(that.addWidgetPrefix(FILTER_RANGE_START_CLASS));
    if (filterValue && Array.isArray(filterValue) && "between" === getColumnSelectedFilterOperation(that, column)) {
        if (isRangeStart) {
            return filterValue[0]
        }
        return filterValue[1]
    }
    return !isFilterRange && isValidFilterValue(filterValue, column) ? filterValue : null
};
var normalizeFilterValue = function(that, filterValue, column, $editorContainer) {
    if ("between" === getColumnSelectedFilterOperation(that, column)) {
        var columnFilterValue = getColumnFilterValue(that, column);
        if ($editorContainer.hasClass(that.addWidgetPrefix(FILTER_RANGE_START_CLASS))) {
            return [filterValue, Array.isArray(columnFilterValue) ? columnFilterValue[1] : void 0]
        }
        return [Array.isArray(columnFilterValue) ? columnFilterValue[0] : columnFilterValue, filterValue]
    }
    return filterValue
};
var updateFilterValue = function(that, options) {
    var value = "" === options.value ? null : options.value;
    var $editorContainer = options.container;
    var column = that._columnsController.columnOption(options.column.index);
    var filterValue = getFilterValue(that, column.index, $editorContainer);
    if (!isDefined(filterValue) && !isDefined(value)) {
        return
    }
    that._applyFilterViewController.setHighLight($editorContainer, filterValue !== value);
    var columnOptionName = isOnClickApplyFilterMode(that) ? "bufferedFilterValue" : "filterValue";
    var normalizedValue = normalizeFilterValue(that, value, column, $editorContainer);
    var isBetween = "between" === getColumnSelectedFilterOperation(that, column);
    var notFireEvent = options.notFireEvent || isBetween && Array.isArray(normalizedValue) && normalizedValue.includes(void 0);
    that._columnsController.columnOption(column.index, columnOptionName, normalizedValue, notFireEvent)
};
var columnHeadersView = Base => class extends Base {
    init() {
        super.init();
        this._applyFilterViewController = this.getController("applyFilter")
    }
    optionChanged(args) {
        switch (args.name) {
            case "filterRow":
            case "showColumnLines":
                this._invalidate(true, true);
                args.handled = true;
                break;
            case "syncLookupFilterValues":
                if (args.value) {
                    this.updateLookupDataSource()
                } else {
                    this.render()
                }
                args.handled = true;
                break;
            default:
                super.optionChanged(args)
        }
    }
    _updateEditorValue(column, $editorContainer) {
        var editor = getEditorInstance($editorContainer);
        editor && editor.option("value", getFilterValue(this, column.index, $editorContainer))
    }
    _columnOptionChanged(e) {
        var _a;
        var {
            optionNames: optionNames
        } = e;
        var $cell;
        var $editorContainer;
        var $editorRangeElements;
        var $menu;
        if (gridCoreUtils.checkChanges(optionNames, ["filterValue", "bufferedFilterValue", "selectedFilterOperation", "bufferedSelectedFilterOperation", "filterValues", "filterType"]) && void 0 !== e.columnIndex) {
            var visibleIndex = this._columnsController.getVisibleIndex(e.columnIndex);
            var column = this._columnsController.columnOption(e.columnIndex);
            $cell = null !== (_a = this._getCellElement(this.element().find(".".concat(this.addWidgetPrefix(FILTER_ROW_CLASS))).index(), visibleIndex)) && void 0 !== _a ? _a : $();
            $editorContainer = $cell.find(".".concat(EDITOR_CONTAINER_CLASS)).first();
            if (optionNames.filterValue || optionNames.bufferedFilterValue) {
                this._updateEditorValue(column, $editorContainer);
                var overlayInstance = $cell.find(".".concat(this.addWidgetPrefix(FILTER_RANGE_OVERLAY_CLASS))).data("dxOverlay");
                if (overlayInstance) {
                    $editorRangeElements = overlayInstance.$content().find(".".concat(EDITOR_CONTAINER_CLASS));
                    this._updateEditorValue(column, $editorRangeElements.first());
                    this._updateEditorValue(column, $editorRangeElements.last())
                }
                if (!overlayInstance || !overlayInstance.option("visible")) {
                    this._updateFilterRangeContent($cell, getRangeTextByFilterValue(this, column))
                }
            }
            if (optionNames.selectedFilterOperation || optionNames.bufferedSelectedFilterOperation) {
                if (visibleIndex >= 0 && column) {
                    $menu = $cell.find(".".concat(MENU_CLASS));
                    if ($menu.length) {
                        this._updateFilterOperationChooser($menu, column, $editorContainer);
                        if ("between" === getColumnSelectedFilterOperation(this, column)) {
                            this._renderFilterRangeContent($cell, column)
                        } else if ($editorContainer.find(".".concat(FILTER_RANGE_CONTENT_CLASS)).length) {
                            this._renderEditor($editorContainer, this._getEditorOptions($editorContainer, column));
                            this._hideFilterRange()
                        }
                    }
                }
            }
            return
        }
        super._columnOptionChanged(e)
    }
    _renderCore() {
        this._filterRangeOverlayInstance = null;
        return super._renderCore.apply(this, arguments)
    }
    _resizeCore() {
        var _a;
        super._resizeCore.apply(this, arguments);
        null === (_a = this._filterRangeOverlayInstance) || void 0 === _a ? void 0 : _a.repaint()
    }
    isFilterRowVisible() {
        return this._isElementVisible(this.option("filterRow"))
    }
    isVisible() {
        return super.isVisible() || this.isFilterRowVisible()
    }
    _initFilterRangeOverlay($cell, column) {
        var that = this;
        var sharedData = {};
        var $editorContainer = $cell.find(".dx-editor-container");
        var filterRangeOverlayClass = that.addWidgetPrefix(FILTER_RANGE_OVERLAY_CLASS);
        var $overlay = $("<div>").addClass(filterRangeOverlayClass).appendTo($cell);
        return that._createComponent($overlay, Overlay, {
            height: "auto",
            shading: false,
            showTitle: false,
            focusStateEnabled: false,
            hideOnOutsideClick: true,
            wrapperAttr: {
                class: filterRangeOverlayClass
            },
            animation: false,
            position: {
                my: "top",
                at: "top",
                of: $editorContainer.length && $editorContainer || $cell,
                offset: "0 -1"
            },
            contentTemplate(contentElement) {
                var editorOptions;
                var $editor = $("<div>").addClass("".concat(EDITOR_CONTAINER_CLASS, " ").concat(that.addWidgetPrefix(FILTER_RANGE_START_CLASS))).appendTo(contentElement);
                column = that._columnsController.columnOption(column.index);
                editorOptions = that._getEditorOptions($editor, column);
                editorOptions.sharedData = sharedData;
                that._renderEditor($editor, editorOptions);
                eventsEngine.on($editor.find(EDITORS_INPUT_SELECTOR), "keydown", e => {
                    var $prevElement = $cell.find("[tabindex]").not(e.target).first();
                    if ("tab" === normalizeKeyName(e) && e.shiftKey) {
                        e.preventDefault();
                        that._hideFilterRange();
                        if (!$prevElement.length) {
                            $prevElement = $cell.prev().find("[tabindex]").last()
                        }
                        eventsEngine.trigger($prevElement, "focus")
                    }
                });
                $editor = $("<div>").addClass("".concat(EDITOR_CONTAINER_CLASS, " ").concat(that.addWidgetPrefix(FILTER_RANGE_END_CLASS))).appendTo(contentElement);
                editorOptions = that._getEditorOptions($editor, column);
                editorOptions.sharedData = sharedData;
                that._renderEditor($editor, editorOptions);
                eventsEngine.on($editor.find(EDITORS_INPUT_SELECTOR), "keydown", e => {
                    if ("tab" === normalizeKeyName(e) && !e.shiftKey) {
                        e.preventDefault();
                        that._hideFilterRange();
                        eventsEngine.trigger($cell.next().find("[tabindex]").first(), "focus")
                    }
                });
                return $(contentElement).addClass(that.getWidgetContainerClass())
            },
            onShown(e) {
                var $editor = e.component.$content().find(".".concat(EDITOR_CONTAINER_CLASS)).first();
                eventsEngine.trigger($editor.find(EDITORS_INPUT_SELECTOR), "focus")
            },
            onHidden() {
                column = that._columnsController.columnOption(column.index);
                $cell.find(".".concat(MENU_CLASS)).parent().addClass(EDITOR_WITH_MENU_CLASS);
                if ("between" === getColumnSelectedFilterOperation(that, column)) {
                    that._updateFilterRangeContent($cell, getRangeTextByFilterValue(that, column));
                    that.component.updateDimensions()
                }
            }
        })
    }
    _updateFilterRangeOverlay(options) {
        var overlayInstance = this._filterRangeOverlayInstance;
        overlayInstance && overlayInstance.option(options)
    }
    _showFilterRange($cell, column) {
        var $overlay = $cell.children(".".concat(this.addWidgetPrefix(FILTER_RANGE_OVERLAY_CLASS)));
        var overlayInstance = $overlay.length && $overlay.data("dxOverlay");
        if (!overlayInstance && column) {
            overlayInstance = this._initFilterRangeOverlay($cell, column)
        }
        if (!overlayInstance.option("visible")) {
            this._filterRangeOverlayInstance && this._filterRangeOverlayInstance.hide();
            this._filterRangeOverlayInstance = overlayInstance;
            this._updateFilterRangeOverlay({
                width: getOuterWidth($cell, true) + CORRECT_FILTER_RANGE_OVERLAY_WIDTH
            });
            this._filterRangeOverlayInstance && this._filterRangeOverlayInstance.show()
        }
    }
    _hideFilterRange() {
        var overlayInstance = this._filterRangeOverlayInstance;
        overlayInstance && overlayInstance.hide()
    }
    getFilterRangeOverlayInstance() {
        return this._filterRangeOverlayInstance
    }
    _createRow(row) {
        var $row = super._createRow(row);
        if ("filter" === row.rowType) {
            $row.addClass(this.addWidgetPrefix(FILTER_ROW_CLASS));
            if (!this.option("useLegacyKeyboardNavigation")) {
                eventsEngine.on($row, "keydown", event => selectView("filterRow", this, event))
            }
        }
        return $row
    }
    _getRows() {
        var result = super._getRows();
        if (this.isFilterRowVisible()) {
            result.push({
                rowType: "filter"
            })
        }
        return result
    }
    _renderFilterCell(cell, options) {
        var {
            column: column
        } = options;
        var $cell = $(cell);
        if (this.component.option("showColumnHeaders")) {
            this.setAria("describedby", column.headerId, $cell)
        }
        this.setAria("label", messageLocalization.format("dxDataGrid-ariaFilterCell"), $cell);
        $cell.addClass(EDITOR_CELL_CLASS);
        var $container = $("<div>").appendTo($cell);
        var $editorContainer = $("<div>").addClass(EDITOR_CONTAINER_CLASS).appendTo($container);
        if ("between" === getColumnSelectedFilterOperation(this, column)) {
            this._renderFilterRangeContent($cell, column)
        } else {
            var editorOptions = this._getEditorOptions($editorContainer, column);
            this._renderEditor($editorContainer, editorOptions)
        }
        var {
            alignment: alignment
        } = column;
        if (alignment && "center" !== alignment) {
            $cell.find(EDITORS_INPUT_SELECTOR).first().css("textAlign", column.alignment)
        }
        if (column.filterOperations && column.filterOperations.length) {
            this._renderFilterOperationChooser($container, column, $editorContainer)
        }
    }
    _renderCellContent($cell, options) {
        var that = this;
        var {
            column: column
        } = options;
        if ("filter" === options.rowType) {
            if (column.command) {
                $cell.html("&nbsp;")
            } else if (column.allowFiltering) {
                that.renderTemplate($cell, that._renderFilterCell.bind(that), options).done(() => {
                    that._updateCell($cell, options)
                });
                return
            }
        }
        super._renderCellContent.apply(this, arguments)
    }
    _getEditorOptions($editorContainer, column) {
        var that = this;
        var accessibilityOptions = {
            editorOptions: {
                inputAttr: that._getFilterInputAccessibilityAttributes(column)
            }
        };
        var result = extend(accessibilityOptions, column, {
            value: getFilterValue(that, column.index, $editorContainer),
            parentType: "filterRow",
            showAllText: that.option("filterRow.showAllText"),
            updateValueTimeout: "onClick" === that.option("filterRow.applyFilter") ? 0 : FILTERING_TIMEOUT,
            width: null,
            setValue(value, notFireEvent) {
                updateFilterValue(that, {
                    column: column,
                    value: value,
                    container: $editorContainer,
                    notFireEvent: notFireEvent
                })
            }
        });
        if ("between" === getColumnSelectedFilterOperation(that, column)) {
            if ($editorContainer.hasClass(that.addWidgetPrefix(FILTER_RANGE_START_CLASS))) {
                result.placeholder = that.option("filterRow.betweenStartText")
            } else {
                result.placeholder = that.option("filterRow.betweenEndText")
            }
        }
        return result
    }
    _getFilterInputAccessibilityAttributes(column) {
        var columnAriaLabel = messageLocalization.format("dxDataGrid-ariaFilterCell");
        if (this.component.option("showColumnHeaders")) {
            return {
                "aria-label": columnAriaLabel,
                "aria-describedby": column.headerId
            }
        }
        return {
            "aria-label": columnAriaLabel
        }
    }
    _renderEditor($editorContainer, options) {
        $editorContainer.empty();
        var $element = $("<div>").appendTo($editorContainer);
        var dataSource = this._dataController.dataSource();
        if (options.lookup && this.option("syncLookupFilterValues")) {
            this._applyFilterViewController.setCurrentColumnForFiltering(options);
            var filter = this._dataController.getCombinedFilter();
            this._applyFilterViewController.setCurrentColumnForFiltering(null);
            var lookupDataSource = gridCoreUtils.getWrappedLookupDataSource(options, dataSource, filter);
            var lookupOptions = _extends(_extends({}, options), {
                lookup: _extends(_extends({}, options.lookup), {
                    dataSource: lookupDataSource
                })
            });
            return this._editorFactoryController.createEditor($element, lookupOptions)
        }
        return this._editorFactoryController.createEditor($element, options)
    }
    _renderFilterRangeContent($cell, column) {
        var that = this;
        var $editorContainer = $cell.find(".".concat(EDITOR_CONTAINER_CLASS)).first();
        $editorContainer.empty();
        var $filterRangeContent = $("<div>").addClass(FILTER_RANGE_CONTENT_CLASS).attr("tabindex", this.option("tabIndex"));
        eventsEngine.on($filterRangeContent, "focusin", () => {
            that._showFilterRange($cell, column)
        });
        $filterRangeContent.appendTo($editorContainer);
        that._updateFilterRangeContent($cell, getRangeTextByFilterValue(that, column))
    }
    _updateFilterRangeContent($cell, value) {
        var $filterRangeContent = $cell.find(".".concat(FILTER_RANGE_CONTENT_CLASS));
        if ($filterRangeContent.length) {
            if ("" === value) {
                $filterRangeContent.html("&nbsp;")
            } else {
                $filterRangeContent.text(value)
            }
        }
    }
    _updateFilterOperationChooser($menu, column, $editorContainer) {
        var that = this;
        var isCellWasFocused;
        var restoreFocus = function() {
            var menu = Menu.getInstance($menu);
            menu && menu.option("focusedElement", null);
            isCellWasFocused && that._focusEditor($editorContainer)
        };
        var editorFactoryController = this._editorFactoryController;
        that._createComponent($menu, Menu, {
            integrationOptions: {},
            activeStateEnabled: false,
            selectionMode: "single",
            cssClass: "".concat(that.getWidgetContainerClass(), " ").concat(CELL_FOCUS_DISABLED_CLASS, " ").concat(FILTER_MENU),
            showFirstSubmenuMode: "onHover",
            hideSubmenuOnMouseLeave: true,
            items: [{
                disabled: !(column.filterOperations && column.filterOperations.length),
                icon: OPERATION_ICONS[getColumnSelectedFilterOperation(that, column) || "default"],
                selectable: false,
                items: that._getFilterOperationMenuItems(column)
            }],
            onItemRendered: _ref => {
                var {
                    itemElement: itemElement
                } = _ref;
                this.setAria("label", ARIA_SEARCH_BOX, $(itemElement))
            },
            onItemClick(properties) {
                var selectedFilterOperation = properties.itemData.name;
                var columnSelectedFilterOperation = getColumnSelectedFilterOperation(that, column);
                var notFocusEditor = false;
                var isOnClickMode = isOnClickApplyFilterMode(that);
                var options = {};
                if (properties.itemData.items || selectedFilterOperation && selectedFilterOperation === columnSelectedFilterOperation) {
                    return
                }
                if (selectedFilterOperation) {
                    options[isOnClickMode ? "bufferedSelectedFilterOperation" : "selectedFilterOperation"] = selectedFilterOperation;
                    if ("between" === selectedFilterOperation || "between" === columnSelectedFilterOperation) {
                        notFocusEditor = "between" === selectedFilterOperation;
                        options[isOnClickMode ? "bufferedFilterValue" : "filterValue"] = null
                    }
                } else {
                    options[isOnClickMode ? "bufferedFilterValue" : "filterValue"] = null;
                    options[isOnClickMode ? "bufferedSelectedFilterOperation" : "selectedFilterOperation"] = column.defaultSelectedFilterOperation || null
                }
                that._columnsController.columnOption(column.index, options);
                that._applyFilterViewController.setHighLight($editorContainer, true);
                if (!selectedFilterOperation) {
                    var editor = getEditorInstance($editorContainer);
                    if (editor && "dxDateBox" === editor.NAME && !editor.option("isValid")) {
                        editor.clear();
                        editor.option("isValid", true)
                    }
                }
                if (!notFocusEditor) {
                    that._focusEditor($editorContainer)
                } else {
                    that._showFilterRange($editorContainer.closest(".".concat(EDITOR_CELL_CLASS)), column)
                }
            },
            onSubmenuShowing() {
                isCellWasFocused = that._isEditorFocused($editorContainer);
                editorFactoryController.loseFocus()
            },
            onSubmenuHiding() {
                eventsEngine.trigger($menu, "blur");
                restoreFocus()
            },
            onContentReady(e) {
                eventsEngine.on($menu, "blur", () => {
                    var menu = e.component;
                    menu._hideSubmenuAfterTimeout();
                    restoreFocus()
                })
            },
            rtlEnabled: that.option("rtlEnabled")
        })
    }
    _isEditorFocused($container) {
        return $container.hasClass(FOCUSED_CLASS) || $container.parents(".".concat(FOCUSED_CLASS)).length
    }
    _focusEditor($container) {
        this._editorFactoryController.focus($container);
        eventsEngine.trigger($container.find(EDITORS_INPUT_SELECTOR), "focus")
    }
    _renderFilterOperationChooser($container, column, $editorContainer) {
        var $menu;
        if (this.option("filterRow.showOperationChooser")) {
            $container.addClass(EDITOR_WITH_MENU_CLASS);
            $menu = $("<div>").prependTo($container);
            this._updateFilterOperationChooser($menu, column, $editorContainer)
        }
    }
    _getFilterOperationMenuItems(column) {
        var that = this;
        var result = [{}];
        var filterRowOptions = that.option("filterRow");
        var operationDescriptions = filterRowOptions && filterRowOptions.operationDescriptions || {};
        if (column.filterOperations && column.filterOperations.length) {
            var availableFilterOperations = column.filterOperations.filter(value => isDefined(OPERATION_DESCRIPTORS[value]));
            result = map(availableFilterOperations, value => {
                var descriptionName = OPERATION_DESCRIPTORS[value];
                return {
                    name: value,
                    selected: (getColumnSelectedFilterOperation(that, column) || column.defaultFilterOperation) === value,
                    text: operationDescriptions[descriptionName],
                    icon: OPERATION_ICONS[value]
                }
            });
            result.push({
                name: null,
                text: filterRowOptions && filterRowOptions.resetOperationText,
                icon: OPERATION_ICONS.default
            })
        }
        return result
    }
    _handleDataChanged(e) {
        var _a, _b, _c, _d, _e, _f;
        var dataSource = null === (_b = null === (_a = this._dataController) || void 0 === _a ? void 0 : _a.dataSource) || void 0 === _b ? void 0 : _b.call(_a);
        var lastLoadOptions = null === (_c = null === dataSource || void 0 === dataSource ? void 0 : dataSource.lastLoadOptions) || void 0 === _c ? void 0 : _c.call(dataSource);
        super._handleDataChanged.apply(this, arguments);
        if ((null === (_d = e.operationTypes) || void 0 === _d ? void 0 : _d.filtering) || (null === (_e = e.operationTypes) || void 0 === _e ? void 0 : _e.fullReload)) {
            this.updateLookupDataSource((null === (_f = e.operationTypes) || void 0 === _f ? void 0 : _f.filtering) || (null === lastLoadOptions || void 0 === lastLoadOptions ? void 0 : lastLoadOptions.filter))
        }
    }
    updateLookupDataSource(filterChanged) {
        if (!this.option("syncLookupFilterValues")) {
            return
        }
        if (!this.element()) {
            return
        }
        var columns = this._columnsController.getVisibleColumns();
        var dataSource = this._dataController.dataSource();
        var applyFilterViewController = this._applyFilterViewController;
        var rowIndex = this.element().find(".".concat(this.addWidgetPrefix(FILTER_ROW_CLASS))).index();
        if (-1 === rowIndex) {
            return
        }
        columns.forEach((column, index) => {
            if (!column.lookup || column.calculateCellValue !== column.defaultCalculateCellValue) {
                return
            }
            var $cell = this._getCellElement(rowIndex, index);
            var editor = getEditorInstance(null === $cell || void 0 === $cell ? void 0 : $cell.find(".dx-editor-container"));
            if (editor) {
                applyFilterViewController.setCurrentColumnForFiltering(column);
                var filter = this._dataController.getCombinedFilter() || null;
                applyFilterViewController.setCurrentColumnForFiltering(null);
                var editorDataSource = editor.option("dataSource");
                var shouldUpdateFilter = !filterChanged || !equalByValue(editorDataSource.__dataGridSourceFilter || null, filter);
                if (shouldUpdateFilter) {
                    var lookupDataSource = gridCoreUtils.getWrappedLookupDataSource(column, dataSource, filter);
                    editor.option("dataSource", lookupDataSource)
                }
            }
        })
    }
};
var data = Base => class extends Base {
    skipCalculateColumnFilters() {
        return false
    }
    _calculateAdditionalFilter() {
        if (this.skipCalculateColumnFilters()) {
            return super._calculateAdditionalFilter()
        }
        var filters = [super._calculateAdditionalFilter()];
        var columns = this._columnsController.getVisibleColumns(null, true);
        var applyFilterController = this._applyFilterController;
        each(columns, (function() {
            var _a;
            var shouldSkip = (null === (_a = applyFilterController.getCurrentColumnForFiltering()) || void 0 === _a ? void 0 : _a.index) === this.index;
            if (this.allowFiltering && this.calculateFilterExpression && isDefined(this.filterValue) && !shouldSkip) {
                var filter = this.createFilterExpression(this.filterValue, this.selectedFilterOperation || this.defaultFilterOperation, "filterRow");
                filters.push(filter)
            }
        }));
        return gridCoreUtils.combineFilters(filters)
    }
};
export class ApplyFilterViewController extends modules.ViewController {
    init() {
        this._columnsController = this.getController("columns")
    }
    _getHeaderPanel() {
        if (!this._headerPanel) {
            this._headerPanel = this.getView("headerPanel")
        }
        return this._headerPanel
    }
    setHighLight($element, value) {
        if (isOnClickApplyFilterMode(this)) {
            $element && $element.toggleClass(HIGHLIGHT_OUTLINE_CLASS, value) && $element.closest(".".concat(EDITOR_CELL_CLASS)).toggleClass(FILTER_MODIFIED_CLASS, value);
            this._getHeaderPanel().enableApplyButton(value)
        }
    }
    applyFilter() {
        var columns = this._columnsController.getColumns();
        this._columnsController.beginUpdate();
        for (var i = 0; i < columns.length; i++) {
            var column = columns[i];
            if (void 0 !== column.bufferedFilterValue) {
                this._columnsController.columnOption(i, "filterValue", column.bufferedFilterValue);
                column.bufferedFilterValue = void 0
            }
            if (void 0 !== column.bufferedSelectedFilterOperation) {
                this._columnsController.columnOption(i, "selectedFilterOperation", column.bufferedSelectedFilterOperation);
                column.bufferedSelectedFilterOperation = void 0
            }
        }
        this._columnsController.endUpdate();
        this.removeHighLights()
    }
    removeHighLights() {
        if (isOnClickApplyFilterMode(this)) {
            var columnHeadersViewElement = this.getView("columnHeadersView").element();
            columnHeadersViewElement.find(".".concat(this.addWidgetPrefix(FILTER_ROW_CLASS), " .").concat(HIGHLIGHT_OUTLINE_CLASS)).removeClass(HIGHLIGHT_OUTLINE_CLASS);
            columnHeadersViewElement.find(".".concat(this.addWidgetPrefix(FILTER_ROW_CLASS), " .").concat(FILTER_MODIFIED_CLASS)).removeClass(FILTER_MODIFIED_CLASS);
            this._getHeaderPanel().enableApplyButton(false)
        }
    }
    setCurrentColumnForFiltering(column) {
        this._currentColumn = column
    }
    getCurrentColumnForFiltering() {
        return this._currentColumn
    }
}
var columnsResizer = Base => class extends Base {
    _startResizing() {
        super._startResizing.apply(this, arguments);
        if (this.isResizing()) {
            var overlayInstance = this._columnHeadersView.getFilterRangeOverlayInstance();
            if (overlayInstance) {
                var cellIndex = overlayInstance.$element().closest("td").index();
                if (cellIndex === this._targetPoint.columnIndex || cellIndex === this._targetPoint.columnIndex + 1) {
                    overlayInstance.$content().hide()
                }
            }
        }
    }
    _endResizing() {
        var $cell;
        if (this.isResizing()) {
            var overlayInstance = this._columnHeadersView.getFilterRangeOverlayInstance();
            if (overlayInstance) {
                $cell = overlayInstance.$element().closest("td");
                this._columnHeadersView._updateFilterRangeOverlay({
                    width: getOuterWidth($cell, true) + CORRECT_FILTER_RANGE_OVERLAY_WIDTH
                });
                overlayInstance.$content().show()
            }
        }
        super._endResizing.apply(this, arguments)
    }
};
var editing = Base => class extends Base {
    updateFieldValue(options) {
        if (options.column.lookup) {
            this._needUpdateLookupDataSource = true
        }
        return super.updateFieldValue.apply(this, arguments)
    }
    _afterSaveEditData(cancel) {
        var _a;
        if (this._needUpdateLookupDataSource && !cancel) {
            null === (_a = this.getView("columnHeadersView")) || void 0 === _a ? void 0 : _a.updateLookupDataSource()
        }
        this._needUpdateLookupDataSource = false;
        return super._afterSaveEditData.apply(this, arguments)
    }
    _afterCancelEditData() {
        this._needUpdateLookupDataSource = false;
        return super._afterCancelEditData.apply(this, arguments)
    }
};
var headerPanel = Base => class extends Base {
    init() {
        super.init();
        this._dataController = this.getController("data");
        this._applyFilterViewController = this.getController("applyFilter")
    }
    optionChanged(args) {
        if ("filterRow" === args.name) {
            this._invalidate();
            args.handled = true
        } else {
            super.optionChanged(args)
        }
    }
    _getToolbarItems() {
        var items = super._getToolbarItems();
        var filterItem = this._prepareFilterItem();
        return filterItem.concat(items)
    }
    _prepareFilterItem() {
        var that = this;
        var filterItem = [];
        if (that._isShowApplyFilterButton()) {
            var hintText = that.option("filterRow.applyFilterText");
            var columns = that._columnsController.getColumns();
            var disabled = !columns.filter(column => void 0 !== column.bufferedFilterValue).length;
            var toolbarItem = {
                widget: "dxButton",
                options: {
                    icon: "apply-filter",
                    disabled: disabled,
                    onClick: function() {
                        that._applyFilterViewController.applyFilter()
                    },
                    hint: hintText,
                    text: hintText,
                    onInitialized: function(e) {
                        $(e.element).addClass(that._getToolbarButtonClass(APPLY_BUTTON_CLASS))
                    }
                },
                showText: "inMenu",
                name: "applyFilterButton",
                location: "after",
                locateInMenu: "auto",
                sortIndex: 10
            };
            filterItem.push(toolbarItem)
        }
        return filterItem
    }
    _isShowApplyFilterButton() {
        var filterRowOptions = this.option("filterRow");
        return !!(null === filterRowOptions || void 0 === filterRowOptions ? void 0 : filterRowOptions.visible) && "onClick" === filterRowOptions.applyFilter
    }
    enableApplyButton(value) {
        this.setToolbarItemDisabled("applyFilterButton", !value)
    }
    isVisible() {
        return super.isVisible() || this._isShowApplyFilterButton()
    }
};
export var filterRowModule = {
    defaultOptions: () => ({
        syncLookupFilterValues: true,
        filterRow: {
            visible: false,
            showOperationChooser: true,
            showAllText: messageLocalization.format("dxDataGrid-filterRowShowAllText"),
            resetOperationText: messageLocalization.format("dxDataGrid-filterRowResetOperationText"),
            applyFilter: "auto",
            applyFilterText: messageLocalization.format("dxDataGrid-applyFilterText"),
            operationDescriptions: {
                equal: messageLocalization.format("dxDataGrid-filterRowOperationEquals"),
                notEqual: messageLocalization.format("dxDataGrid-filterRowOperationNotEquals"),
                lessThan: messageLocalization.format("dxDataGrid-filterRowOperationLess"),
                lessThanOrEqual: messageLocalization.format("dxDataGrid-filterRowOperationLessOrEquals"),
                greaterThan: messageLocalization.format("dxDataGrid-filterRowOperationGreater"),
                greaterThanOrEqual: messageLocalization.format("dxDataGrid-filterRowOperationGreaterOrEquals"),
                startsWith: messageLocalization.format("dxDataGrid-filterRowOperationStartsWith"),
                contains: messageLocalization.format("dxDataGrid-filterRowOperationContains"),
                notContains: messageLocalization.format("dxDataGrid-filterRowOperationNotContains"),
                endsWith: messageLocalization.format("dxDataGrid-filterRowOperationEndsWith"),
                between: messageLocalization.format("dxDataGrid-filterRowOperationBetween"),
                isBlank: messageLocalization.format("dxFilterBuilder-filterOperationIsBlank"),
                isNotBlank: messageLocalization.format("dxFilterBuilder-filterOperationIsNotBlank")
            },
            betweenStartText: messageLocalization.format("dxDataGrid-filterRowOperationBetweenStartText"),
            betweenEndText: messageLocalization.format("dxDataGrid-filterRowOperationBetweenEndText")
        }
    }),
    controllers: {
        applyFilter: ApplyFilterViewController
    },
    extenders: {
        controllers: {
            data: data,
            columnsResizer: columnsResizer,
            editing: editing
        },
        views: {
            columnHeadersView: columnHeadersView,
            headerPanel: headerPanel
        }
    }
};
